import React from "react";
import { CalendarClock, CalendarCog } from "lucide-react";
import { useTranslation } from "react-i18next";

export type SessionEditScope = "series" | "occurrence";

interface EditScopeSwitchProps {
  scope: SessionEditScope;
  onChange: (scope: SessionEditScope) => void;
}

/**
 * Segmented scope selector shown inside the editor of a recurring session:
 * the regular form edits the entire series; "single occurrence" moves one date.
 */
const EditScopeSwitch: React.FC<EditScopeSwitchProps> = ({ scope, onChange }) => {
  const { t } = useTranslation();

  const optionClass = (active: boolean) =>
    `inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
      active
        ? "bg-primary-90 text-primary-30 dark:bg-primary-30 dark:text-primary-90"
        : "text-grey-30 hover:bg-grey-95 dark:text-grey-70 dark:hover:bg-primary-20"
    }`;

  return (
    <div
      role="tablist"
      aria-label={t("sessions.editScope.scopeSeries")}
      className="inline-flex items-center gap-1 rounded-xl border border-grey-90 p-1 dark:border-primary-20"
    >
      <button
        type="button"
        role="tab"
        aria-selected={scope === "series"}
        className={optionClass(scope === "series")}
        onClick={() => onChange("series")}
      >
        <CalendarCog size={16} />
        <span>{t("sessions.editScope.scopeSeries")}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={scope === "occurrence"}
        className={optionClass(scope === "occurrence")}
        onClick={() => onChange("occurrence")}
      >
        <CalendarClock size={16} />
        <span>{t("sessions.editScope.scopeOccurrence")}</span>
      </button>
    </div>
  );
};

export default EditScopeSwitch;
